import { useMemo, useReducer } from 'react';
import { defaultAssessmentRunsFormValues, type AssessmentRunsFormData } from './AssessmentRunsFormConfig';

export type FormMode = 'create' | 'edit';
// State interface
export interface AssessmentRunsFormState {
    currentStep: number;
    formData: AssessmentRunsFormData;
    mode: FormMode;
    isSubmitting: boolean;
}

// Actions interface
export interface AssessmentRunsFormActions {
    updateFormData: (data: Partial<AssessmentRunsFormData>) => void;
    submit: (data: Partial<AssessmentRunsFormData>) => void;
    submitStart: () => void;
    submitSuccess: () => void;
    submitError: () => void;
    reset: () => void;
    initialize: (data: Partial<AssessmentRunsFormData>) => void;
}

type AssessmentRunsFormAction =
    | { type: 'NEXT_STEP'; payload?: Partial<AssessmentRunsFormData> }
    | { type: 'PREV_STEP' }
    | { type: 'SET_STEP'; payload: number }
    | { type: 'UPDATE_FORM_DATA'; payload: Partial<AssessmentRunsFormData> }
    | { type: 'SUBMIT'; payload: Partial<AssessmentRunsFormData> }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_SUCCESS' }
    | { type: 'SUBMIT_ERROR' }
    | { type: 'RESET' }
    | { type: 'INITIALIZE'; payload: Partial<AssessmentRunsFormData> };

function assessmentRunsFormReducer(
    state: AssessmentRunsFormState,
    action: AssessmentRunsFormAction
): AssessmentRunsFormState {
    switch (action.type) {
        case 'NEXT_STEP':
            return {
                ...state,
                currentStep: state.currentStep + 1,
                formData: action.payload ? { ...state.formData, ...action.payload } : state.formData,
            };
        case 'PREV_STEP':
            return {
                ...state,
                currentStep: Math.max(1, state.currentStep - 1),
            };
        case 'SET_STEP':
            return {
                ...state,
                currentStep: action.payload,
            };
        case 'UPDATE_FORM_DATA':
            return {
                ...state,
                formData: { ...state.formData, ...action.payload },
            };
        case 'SUBMIT':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_START':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_SUCCESS':
        case 'SUBMIT_ERROR':
            return {
                ...state,
                isSubmitting: false,
            };
        case 'RESET':
            return {
                ...state,
                formData: defaultAssessmentRunsFormValues,
                isSubmitting: false,
            };
        case 'INITIALIZE':
            return {
                ...state,
                formData: { ...defaultAssessmentRunsFormValues, ...action.payload },
            };
        default:
            return state;
    }
}

export function useAssessmentRunsFormState(mode: FormMode, initialData?: Partial<AssessmentRunsFormData>) {
    // Initialize state
    const initialState: AssessmentRunsFormState = {
        currentStep: 1,
        formData: initialData
            ? { ...defaultAssessmentRunsFormValues, ...initialData }
            : defaultAssessmentRunsFormValues,
        mode,
        isSubmitting: false,
    };

    // Create reducer
    const [state, dispatch] = useReducer(assessmentRunsFormReducer, initialState);
    const actions = useMemo(
        () => ({
            nextStep: (data?: Partial<AssessmentRunsFormData>) => dispatch({ type: 'NEXT_STEP', payload: data }),
            prevStep: () => dispatch({ type: 'PREV_STEP' }),
            setStep: (step: number) => dispatch({ type: 'SET_STEP', payload: step }),
            updateFormData: (data: Partial<AssessmentRunsFormData>) =>
                dispatch({ type: 'UPDATE_FORM_DATA', payload: data }),
            submit: (data: Partial<AssessmentRunsFormData>) => dispatch({ type: 'SUBMIT', payload: data }),
            submitStart: () => dispatch({ type: 'SUBMIT_START' }),
            submitSuccess: () => dispatch({ type: 'SUBMIT_SUCCESS' }),
            submitError: () => dispatch({ type: 'SUBMIT_ERROR' }),
            reset: () => dispatch({ type: 'RESET' }),
            initialize: (data: Partial<AssessmentRunsFormData>) => dispatch({ type: 'INITIALIZE', payload: data }),
        }),
        [dispatch]
    );

    return { state, actions };
}
